// ============================================================================
// Compatibility layer over the global Zustand store (../store.js).
// These hooks keep the SAME return signatures the pages already use, so no
// page code needs to change - but data now flows from a single shared store
// with one realtime subscription set, so figures stay in sync across pages.
// ============================================================================
import { useStore, selectTotalAssets, selectTotalDebt, selectTotalInvestments, selectMonthlyContributions, monthlyIncomeForMonth } from '../store'
import { incomeOccursInMonth, incomeMonthlyEquivalent } from '../lib/payday'

// ── Accounts ──────────────────────────────────────────────
export function useAccounts() {
  const accounts = useStore(s => s.accounts)
  const loading = useStore(s => s.loading.accounts)
  const upsertAccount = useStore(s => s.upsertAccount)
  const deleteAccount = useStore(s => s.deleteAccount)
  const refresh = useStore(s => s.fetchAccounts)
  const totalAssets = accounts.reduce((sum, a) => sum + (a.balance || 0), 0)
  return { accounts, loading, upsertAccount, deleteAccount, totalAssets, refresh }
}

// ── Budgets (still month-scoped, fetched locally) ─────────
// Budgets are month-keyed and not part of the shared realtime set, so this hook
// keeps its own local fetch. Kept here for signature compatibility.
import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'
import { billMonthlyEquivalent, billDueDateInMonth } from '../lib/payday'

export function useBudgets(month) {
  const { user } = useAuth()
  const [budgets, setBudgets] = useState([])
  const [loading, setLoading] = useState(true)

  const fetch = useCallback(async () => {
    if (!user) return
    // No cycle key yet (profile / pay day still loading): render empty instead of
    // hanging on the loading state forever. Refetches once `month` resolves.
    if (!month) { setBudgets([]); setLoading(false); return }
    const { data } = await supabase.from('budgets').select('*').eq('user_id', user.id).eq('month', month).order('category')
    setBudgets(data || [])
    setLoading(false)
  }, [user, month])

  useEffect(() => { fetch() }, [fetch])

  async function saveBudget(category, budgeted, spent, bucket) {
    const row = { user_id: user.id, month, category, budgeted, spent }
    if (bucket !== undefined) row.bucket = bucket || null
    const { error } = await supabase.from('budgets')
      .upsert(row, { onConflict: 'user_id,month,category' })
    if (!error) await fetch()
    return { error }
  }

  async function deleteBudget(category) {
    await supabase.from('budgets').delete().eq('user_id', user.id).eq('month', month).eq('category', category)
    await fetch()
  }

  const totalBudgeted = budgets.reduce((s, b) => s + (b.budgeted || 0), 0)
  const totalSpent = totalBudgeted
  return { budgets, loading, saveBudget, deleteBudget, totalBudgeted, totalSpent, refresh: fetch }
}

// ── Debts ─────────────────────────────────────────────────
export function useDebts() {
  const debts = useStore(s => s.debts)
  const loading = useStore(s => s.loading.debts)
  const upsertDebt = useStore(s => s.upsertDebt)
  const deleteDebt = useStore(s => s.deleteDebt)
  const refresh = useStore(s => s.fetchDebts)
  const totalDebt = debts.reduce((s, d) => s + (d.current_balance || 0), 0)
  const monthlyPayments = debts.reduce((s, d) => s + (d.monthly_payment || 0), 0)
  return { debts, loading, upsertDebt, deleteDebt, totalDebt, monthlyPayments, refresh }
}

// ── Goals ─────────────────────────────────────────────────
export function useGoals() {
  const goals = useStore(s => s.goals)
  const loading = useStore(s => s.loading.goals)
  const upsertGoal = useStore(s => s.upsertGoal)
  const deleteGoal = useStore(s => s.deleteGoal)
  const refresh = useStore(s => s.fetchGoals)
  return { goals, loading, upsertGoal, deleteGoal, refresh }
}

// ── Net Worth Snapshots ───────────────────────────────────
export function useNetWorthHistory() {
  const history = useStore(s => s.netWorthHistory)
  const saveSnapshot = useStore(s => s.saveSnapshot)
  const refresh = useStore(s => s.fetchNetWorthHistory)
  // saveSnapshot signature kept as (date, totalAssets, totalDebts); date is
  // ignored (store derives today's key), matching prior behaviour.
  const wrapped = (date, totalAssets, totalDebts) => saveSnapshot(totalAssets, totalDebts)
  return { history, saveSnapshot: wrapped, refresh }
}

// ── Bills ─────────────────────────────────────────────────

export function useQuickIncome(currentPayCycleStart) {
  const allIncome = useStore(s => s.quickIncome)
  const addQuickIncome = useStore(s => s.addQuickIncome)
  const updateQuickIncome = useStore(s => s.updateQuickIncome)
  const deleteQuickIncome = useStore(s => s.deleteQuickIncome)
  const cycleIncome = currentPayCycleStart
    ? allIncome.filter(e => e.pay_cycle_start === currentPayCycleStart)
    : []
  const cycleTotal = cycleIncome.reduce((s, e) => s + (e.amount || 0), 0)
  return { cycleIncome, cycleTotal, addQuickIncome, updateQuickIncome, deleteQuickIncome }
}

export function useBills() {
  const bills = useStore(s => s.bills)
  const loading = useStore(s => s.loading.bills)
  const upsertBill = useStore(s => s.upsertBill)
  const deleteBill = useStore(s => s.deleteBill)
  const refresh = useStore(s => s.fetchBills)

  // Monthly-equivalent total: annual/quarterly/every-N amortised to a per-month
  // figure so the headline "per month" number stays meaningful.
  const totalMonthly = bills.reduce((s, b) => s + billMonthlyEquivalent(b), 0)

  // Upcoming = bills whose next actual occurrence is within the next ~45 days,
  // respecting each bill's schedule (an annual bill only shows near its month).
  function getUpcoming() {
    const today = new Date()
    const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    const out = []
    for (const b of bills) {
      if (!b.due_day) continue
      // Probe this month and the next two for the bill's next occurrence.
      let occ = null
      for (let i = 0; i < 3; i++) {
        const probe = new Date(today.getFullYear(), today.getMonth() + i, 1)
        const d = billDueDateInMonth(b, probe.getFullYear(), probe.getMonth())
        if (d && d >= t0) { occ = d; break }
      }
      if (!occ) continue
      const daysUntil = Math.round((occ - t0) / (1000 * 60 * 60 * 24))
      if (daysUntil <= 45) out.push({ ...b, daysUntil, nextDate: occ })
    }
    return out.sort((a, b) => a.daysUntil - b.daysUntil)
  }

  return { bills, loading, upsertBill, deleteBill, totalMonthly, getUpcoming, refresh }
}

// ── Monthly Reports ───────────────────────────────────────
export function useMonthlyReports() {
  const { user } = useAuth()
  const [reports, setReports] = useState([])
  const saveReport = useStore(s => s.saveReport)

  const fetch = useCallback(async () => {
    if (!user) return
    const { data } = await supabase.from('monthly_reports').select('*').eq('user_id', user.id).order('month', { ascending: false }).limit(13)
    setReports(data || [])
  }, [user])

  useEffect(() => { fetch() }, [fetch])

  const wrapped = async (month, data) => { await saveReport(month, data); await fetch() }
  return { reports, saveReport: wrapped, refresh: fetch }
}

// ── Investments ───────────────────────────────────────────
export function useInvestments() {
  const investments = useStore(s => s.investments)
  const loading = useStore(s => s.loading.investments)
  const snapshots = useStore(s => s.investmentSnapshots)
  const upsertInvestment = useStore(s => s.upsertInvestment)
  const deleteInvestment = useStore(s => s.deleteInvestment)
  const saveSnapshot = useStore(s => s.saveInvestmentSnapshot)
  const refresh = useStore(s => s.fetchInvestments)
  const totalValue = investments.reduce((s, i) => s + (i.current_value || 0), 0)
  const monthlyContributions = investments.reduce((s, i) => s + (i.monthly_contribution || 0), 0)
  return { investments, loading, snapshots, upsertInvestment, deleteInvestment, saveSnapshot, totalValue, monthlyContributions, refresh }
}

// ── Income (new - dated, multi-source) ────────────────────
export function useIncome() {
  const incomeEntries = useStore(s => s.incomeEntries)
  const loading = useStore(s => s.loading.income)
  const upsertIncomeEntry = useStore(s => s.upsertIncomeEntry)
  const deleteIncomeEntry = useStore(s => s.deleteIncomeEntry)
  const refresh = useStore(s => s.fetchIncomeEntries)

  // Income for a given 'YYYY-MM' = recurring sources + one-offs received that month
  const incomeForMonth = (monthKey) => monthlyIncomeForMonth(incomeEntries, monthKey)

  // Income for a pay cycle (preferred for cycle-keyed budgeting). Counts:
  // Income for a given pay cycle. Includes:
  //   - recurring sources that occur in the cycle's representative month
  //   - one-off entries received between cycle.start and cycle.end (inclusive)
  // Pass a cycle object from getPayCycle / getCycleByEndMonth in payday.js.
  const incomeForCycle = (cycle) => {
    if (!cycle) return 0
    const endDate = new Date(cycle.endIso)
    const startDate = new Date(cycle.startIso)
    const year = endDate.getFullYear()
    const monthIndex = endDate.getMonth()
    return incomeEntries.reduce((total, e) => {
      // Check effective date range
      if (e.started_at && new Date(e.started_at) > endDate) return total
      if (e.ended_at && new Date(e.ended_at) < startDate) return total

      if (e.recurring) {
        if (incomeOccursInMonth(e, year, monthIndex)) return total + (e.amount || 0)
        return total
      }
      const received = (e.received_on || '').slice(0, 10)
      if (received >= cycle.startIso && received <= cycle.endIso) return total + (e.amount || 0)
      return total
    }, 0)
  }

  // Amortised monthly equivalent - only count currently active income
  const today = new Date()
  const recurringMonthly = incomeEntries
    .filter(e => e.recurring)
    .filter(e => {
      if (e.started_at && new Date(e.started_at) > today) return false
      if (e.ended_at && new Date(e.ended_at) < today) return false
      return true
    })
    .reduce((s, e) => s + incomeMonthlyEquivalent(e), 0)

  return { incomeEntries, loading, upsertIncomeEntry, deleteIncomeEntry, incomeForMonth, incomeForCycle, recurringMonthly, refresh }
}

// ── Quick-spends ──────────────────────────────────────────
// One-tap "spent" entries that decrement the safe-to-spend pot. Auto-reset by
// pay cycle. Caller passes the current pay_cycle_start (an ISO date) so we can
// filter to this cycle and compute the running total.
export function useQuickSpends(currentPayCycleStart) {
  const allSpends = useStore(s => s.quickSpends)
  const loading = useStore(s => s.loading.quickSpends)
  const addQuickSpend = useStore(s => s.addQuickSpend)
  const updateQuickSpend = useStore(s => s.updateQuickSpend)
  const deleteQuickSpend = useStore(s => s.deleteQuickSpend)
  const clearQuickSpendsForCycle = useStore(s => s.clearQuickSpendsForCycle)
  const refresh = useStore(s => s.fetchQuickSpends)

  // Scope to the active cycle. Older entries stay in the table but are hidden.
  const cycleSpends = currentPayCycleStart
    ? allSpends.filter(s => s.pay_cycle_start === currentPayCycleStart)
    : []
  const cycleTotal = cycleSpends.reduce((s, e) => s + (e.amount || 0), 0)

  // Categorised quick spends grouped by budget_category for the current cycle.
  // Returns { [category]: total } for entries that have a category tag.
  const categorySpends = cycleSpends.reduce((acc, e) => {
    if (e.budget_category) {
      acc[e.budget_category] = (acc[e.budget_category] || 0) + (e.amount || 0)
    }
    return acc
  }, {})

  return {
    cycleSpends, cycleTotal, categorySpends, loading,
    addQuickSpend, updateQuickSpend, deleteQuickSpend, clearQuickSpendsForCycle,
    refresh,
  }
}
